<div class="page-container">
  @if (!authService.isLoggedIn()) {
    <!-- ── Hero ── -->
    <section class="bg-gradient-primary text-white -mt-4 pb-16 md:pb-20">
      <div class="max-w-7xl mx-auto px-6 pt-16 md:pt-24 text-center">
        <h1 class="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
          Systematic Literature Reviews,<br class="hidden sm:block" />
          Made Collaborative
        </h1>
        <p class="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10">
          Scholaris guides your research team through every stage of a systematic review, from paper
          discovery to data extraction, all in one place.
        </p>
        <div class="flex gap-4 justify-center flex-wrap">
          <a
            routerLink="/register"
            class="px-8 py-3.5 text-lg bg-white text-secondary font-bold rounded-xl hover:bg-gray-100 transition-all shadow-lg shadow-black/20 inline-block"
            >Get Started Free</a
          >
          <a
            routerLink="/login"
            class="px-8 py-3.5 text-lg border-2 border-white/40 text-white font-semibold rounded-xl hover:bg-white/10 transition-all inline-block"
            >Sign In</a
          >
        </div>
      </div>
    </section>

    <!-- ── PRISMA 2020 ── -->
    <section class="bg-white border-t border-b border-slate-100">
      <div class="max-content-width px-6 py-16 md:py-20">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Systematic Reviews, Built on PRISMA 2020
          </h2>
          <p class="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            PRISMA 2020 is the internationally recognised standard for conducting and reporting
            systematic literature reviews. Following PRISMA ensures your review is transparent,
            reproducible, and methodologically sound, requirements for publication in top journals
            and for evidence-based decision making.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div class="card card-padding text-center">
            <div
              class="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-700"
            >
              T
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Transparent</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Every inclusion and exclusion decision is documented. Your entire review trail, from
              initial search to final inclusion, is recorded for scrutiny.
            </p>
          </div>
          <div class="card card-padding text-center">
            <div
              class="w-12 h-12 mx-auto mb-4 rounded-xl bg-emerald-100 flex items-center justify-center text-xl font-bold text-emerald-700"
            >
              R
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Reproducible</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Your protocol, search strategy, screening criteria, and extraction schema are stored
              and versioned, enabling others to verify or replicate your results.
            </p>
          </div>
          <div class="card card-padding text-center">
            <div
              class="w-12 h-12 mx-auto mb-4 rounded-xl bg-purple-100 flex items-center justify-center text-xl font-bold text-purple-700"
            >
              P
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Publishable</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              PRISMA-compliant reviews meet the reporting standards expected by major academic
              journals, increasing the credibility and impact of your work.
            </p>
          </div>
        </div>
        <p class="text-center text-gray-500 text-sm mt-8">
          Scholaris maps directly to the PRISMA 2020 flow diagram, guiding you from record discovery
          through screening to final inclusion.
        </p>
      </div>
    </section>

    <!-- ── Workflow Timeline ── -->
    <section class="bg-slate-50 border-b border-slate-100">
      <div class="max-content-width px-6 py-16 md:py-20 spaced-sections">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Your Review, Step by Step
          </h2>
          <p class="text-gray-600 text-lg max-w-xl mx-auto">
            Scholaris structures your systematic review into six clear stages, keeping your team
            aligned from start to finish.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          @for (stage of workflowStages; track stage.number) {
            <div
              class="card card-padding text-center group hover:shadow-lg hover:border-secondary/30 transition-all"
            >
              <div
                class="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-secondary/10 to-hover/10 flex items-center justify-center text-2xl font-bold text-secondary"
              >
                {{ stage.number }}
              </div>
              <h3 class="font-bold text-gray-900 text-sm mb-1">{{ stage.title }}</h3>
              <p class="text-xs text-gray-500 leading-relaxed">{{ stage.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ── Key Capabilities ── -->
    <section class="bg-slate-50 border-t border-slate-100">
      <div class="max-content-width px-6 py-16 md:py-20 spaced-sections">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
          <p class="text-gray-600 text-lg max-w-xl mx-auto">
            Purpose-built tools for every phase of your systematic review.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3 font-bold text-blue-700 text-lg"
            >
              P
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Multi-Source Import</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Pull papers from OpenAlex, upload BibTeX files, or drag-and-drop PDFs. Built-in
              deduplication keeps your corpus clean.
            </p>
          </div>
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center mb-3 font-bold text-yellow-700 text-lg"
            >
              S
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Dual-Phase Screening</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Title/abstract and full-text screening with multi-reviewer assignment and automatic
              conflict detection.
            </p>
          </div>
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-3 font-bold text-purple-700 text-lg"
            >
              A
            </div>
            <h3 class="font-bold text-gray-900 mb-2">In-Browser PDF Annotation</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Highlight, tag, and annotate PDFs directly in your browser. Annotations are linked to
              papers and visible to your team.
            </p>
          </div>
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 font-bold text-emerald-700 text-lg"
            >
              D
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Configurable Extraction</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Define your own extraction schema with text, choice, and numeric questions. Multiple
              extractors per paper with conflict resolution.
            </p>
          </div>
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center mb-3 font-bold text-orange-700 text-lg"
            >
              T
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Team Collaboration</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Research groups with roles (PI, Researcher), reading lists, and real-time
              notifications keep everyone aligned.
            </p>
          </div>
          <div class="card card-padding hover:shadow-md transition-shadow">
            <div
              class="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center mb-3 font-bold text-red-700 text-lg"
            >
              R
            </div>
            <h3 class="font-bold text-gray-900 mb-2">Reproducible Protocol</h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              Document research questions, search strategy, and inclusion criteria. Export a
              complete record of your review.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Team Collaboration Focus ── -->
    <section class="bg-slate-50 border-t border-slate-100">
      <div class="max-content-width px-6 py-16 md:py-20">
        <div class="max-w-2xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Built for Research Teams
          </h2>
          <p class="text-gray-600 text-lg mb-8 leading-relaxed">
            Scholaris is a shared workspace. Assign screeners, track progress, resolve conflicts,
            and keep your entire review process transparent.
          </p>
          <ul class="space-y-3 text-left inline-block">
            <li class="flex items-start gap-2 text-gray-700">
              <span class="text-secondary font-bold mt-0.5">&check;</span>
              Multi-reviewer assignment with Cohen's Kappa tracking
            </li>
            <li class="flex items-start gap-2 text-gray-700">
              <span class="text-secondary font-bold mt-0.5">&check;</span>
              Role-based access control (PI, Researcher, Admin)
            </li>
            <li class="flex items-start gap-2 text-gray-700">
              <span class="text-secondary font-bold mt-0.5">&check;</span>
              Conflict resolution for screening and extraction disagreements
            </li>
            <li class="flex items-start gap-2 text-gray-700">
              <span class="text-secondary font-bold mt-0.5">&check;</span>
              Real-time notifications and reading list deadlines
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ── Final CTA ── -->
    <section class="bg-gradient-primary text-white">
      <div class="max-content-width px-6 py-16 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-4">Ready to Unfragment Your Research?</h2>
        <p class="text-white/80 text-lg mb-8 max-w-lg mx-auto">
          Create your free account and start your first systematic review today.
        </p>
        <a
          routerLink="/register"
          class="inline-block px-10 py-4 text-lg bg-white text-secondary font-bold rounded-xl hover:bg-gray-100 transition-all shadow-lg shadow-black/20"
          >Create Free Account</a
        >
      </div>
    </section>
  } @else {
    <!-- Authenticated Content -->
    <div class="max-content-width spaced-sections">
      @if (loading) {
        <div class="flex items-center justify-center h-64">
          <div class="text-gray-600">Loading your groups and projects...</div>
        </div>
      } @else if (groups.length === 0) {
        <!-- User has no groups yet -->
        <div class="card card-padding">
          <div class="text-center py-12">
            <div class="text-6xl mb-4">👥</div>
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Welcome to Scholaris!</h2>
            <p class="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Get started by creating your first research group. Groups help you organize projects
              and collaborate with team members.
            </p>
            <button
              type="button"
              (click)="showGroupModal = true"
              class="btn-primary text-lg px-8 py-3"
            >
              Create Your First Group
            </button>
          </div>
        </div>
      } @else {
        <!-- User has groups and projects -->
        <div>
          <h1 class="page-title">Welcome Back!</h1>
          <p class="page-subtitle">Here are your groups and recent projects.</p>
        </div>

        <!-- Groups Section -->
        <section class="card card-padding">
          <div class="flex items-center justify-between mb-4">
            <h2 class="section-title">Your Groups</h2>
            <button type="button" (click)="showGroupModal = true" class="btn-primary">
              + Create Group
            </button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            @for (group of groups; track group.id) {
              <button
                type="button"
                (click)="viewGroup(group)"
                class="rounded-lg border border-slate-200 bg-slate-50 p-4 hover:shadow-md hover:border-secondary transition-all text-left cursor-pointer group"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <h3
                      class="font-bold text-gray-900 group-hover:text-secondary transition-colors"
                    >
                      {{ group.name }}
                    </h3>
                    <p class="text-sm text-gray-600 mt-1 line-clamp-2">
                      {{ group.description || 'No description provided' }}
                    </p>
                  </div>
                  <div
                    class="ml-2 flex-shrink-0 text-secondary opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    →
                  </div>
                </div>
                <div class="text-xs text-gray-500 mt-3">
                  Created {{ group.createdAt | date: 'short' }}
                </div>
              </button>
            }
          </div>
        </section>

        <!-- Projects Section -->
        @if (projects.length > 0) {
          <section class="rounded-2xl border border-slate-200 bg-white shadow-sm mb-4">
            <div class="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 class="text-xl font-bold text-gray-900">Recent Projects</h2>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  (click)="viewGroupProjects()"
                  class="text-sm font-semibold text-secondary hover:underline mr-4"
                >
                  View all →
                </button>
              </div>
            </div>
            <div class="space-y-0">
              @for (project of projects; track project.id) {
                <app-project-card
                  class="border-b border-slate-200 last:border-0 px-2"
                  [project]="project"
                  [isInline]="true"
                ></app-project-card>
              }
            </div>
          </section>
        } @else {
          <section class="rounded-2xl border border-slate-200 bg-white shadow-sm mb-4">
            <div class="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 class="text-xl font-bold text-gray-900">Projects</h2>
            </div>
            <div class="px-6 py-10 text-center">
              <p class="text-gray-600">No projects in this group yet. Create one to get started.</p>
            </div>
          </section>
        }
      }
    </div>
  }
</div>

<app-group-modal
  [open]="showGroupModal"
  (openChange)="showGroupModal = $event"
  (created)="onGroupCreated($event)"
></app-group-modal>
